import { Close as CloseIcon } from "@mui/icons-material";
import { Box, List, ListItem, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  TALENT_OFFER_ACCEPTED,
  TALENT_OFFER_DECLINED,
  TALENT_OFFER_OFFERED,
} from "../../constants";
import { TalentWorkOfferStatus } from "../../orderAPI";
import { useAppSelector } from "../../../../app/hooks";
import { SpinnerButton } from "../../../../common/components/functional/spinnerButton/SpinnerButton";
import { Link } from "react-router-dom";
import { getName } from "../../../../common/helpers/common";
import { isFormerUser } from "../../isFormerUser";

type Props = {
  talents?: TalentWorkOfferStatus[];
  header: string;
  cancelOffer: (talentId: string) => void;
  sendOffer: (talent: TalentWorkOfferStatus) => void;
  visible?: boolean;
};

const ManualOfferTalentList = ({
  talents,
  header,
  cancelOffer,
  sendOffer,
  visible = true,
}: Props) => {
  const { t } = useTranslation();
  const { status: workOffersStatus } = useAppSelector(
    (state) => state.workOffers,
  );
  const isLoading = workOffersStatus === "pending";

  if (!visible) return null;

  const getTalentActionButton = (talent: TalentWorkOfferStatus) => {
    switch (talent.workOfferStatus) {
      case TALENT_OFFER_OFFERED:
        return (
          <SpinnerButton
            label={t("order:cancelShiftOffer")}
            onClick={() => cancelOffer(talent.id)}
            variant="outlined"
            isLoading={isLoading}
            color="primary"
            disabled={!talent.manuallyOffered}
            sx={{ height: "30px", color: "#00000061" }}
            startIcon={<CloseIcon />}
          />
        );
      case TALENT_OFFER_ACCEPTED:
        return (
          <Typography
            sx={{ userSelect: "none" }}
            variant="header"
            fontWeight={700}
            fontSize="14px"
            lineHeight="30px"
            color="success.main"
          >
            {t("order:acceptedOffer")}
          </Typography>
        );
      case TALENT_OFFER_DECLINED:
        return (
          <Typography
            sx={{ userSelect: "none" }}
            variant="header"
            fontWeight={700}
            fontSize="14px"
            lineHeight="30px"
            color="warning.main"
          >
            {t("order:declinedOffer")}
          </Typography>
        );
      default:
        return (
          <SpinnerButton
            label={t("order:offerShift")}
            onClick={() => sendOffer(talent)}
            variant="outlined"
            isLoading={isLoading}
            color="primary"
            sx={{ height: "30px" }}
          />
        );
    }
  };

  return (
    <Box marginBottom="16px">
      <Typography fontWeight={600} marginBottom="8px">
        {header}
      </Typography>
      <List
        sx={{
          "& > li:nth-child(odd)": {
            backgroundColor: "#FAFAFB",
          },
        }}
      >
        {talents ? (
          talents.length > 0 ? (
            talents.map((talent) => (
              <ListItem
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography color="primary.main">
                  <Link
                    to={`/home/talents/${talent.id}/profile`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    {isFormerUser(talent)
                      ? t("order:formerUser")
                      : getName(talent)}
                  </Link>
                </Typography>

                {getTalentActionButton(talent)}
              </ListItem>
            ))
          ) : (
            <Typography>{t("order:noSuitableTalentsFound")}</Typography>
          )
        ) : (
          <Typography>{t("order:inputNameToSearch")}</Typography>
        )}
      </List>
    </Box>
  );
};

export default ManualOfferTalentList;
